import { useState, useEffect, useCallback, memo } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import AnimacionCarga from "./animacion_de_carga";
import BarraSuperior from "./barra_superior";
import BotonSOS from "./sos/boton_sos";
import { obtenerTickets } from "../../functions/consultas";
import "bootstrap-icons/font/bootstrap-icons.css";


const COLORES = [
  "rgb(52, 152, 219)", "rgb(231, 76, 60)", "rgb(46, 204, 113)",
  "rgb(241, 196, 15)", "rgb(155, 89, 182)", "rgb(230, 126, 34)",
  "rgb(26, 188, 156)", "rgb(149, 165, 166)",
];

const COLOR_ESTADO = {
  "Pendiente": "rgb(231, 76, 60)",
  "En Proceso": "rgb(243, 156, 18)",
  "Requisición de compras": "rgb(155, 89, 182)",
  "Desarrollo de software": "rgb(41, 128, 185)",
  "Finalizado": "rgb(39, 174, 96)",
};

const contarPor = (tickets, campo) => {
  const conteo = {};
  tickets.forEach(t => {
    const clave = t[campo] || "Sin dato";
    conteo[clave] = (conteo[clave] || 0) + 1;
  });
  return conteo;
};

const promedioDiasAtencion = (tickets) => {
  const finalizados = tickets.filter(t => t.fecha_del_reporte && t.fecha_fin_solucion);
  if (finalizados.length === 0) return null;
  const totalDias = finalizados.reduce((acc, t) => {
    const inicio = new Date(t.fecha_del_reporte);
    const fin = new Date(t.fecha_fin_solucion);
    const dias = Math.max(0, (fin - inicio) / (1000 * 60 * 60 * 24));
    return acc + dias;
  }, 0);
  return (totalDias / finalizados.length).toFixed(1);
};

const GraficaTorta = ({ conteo, colores }) => {
  const etiquetas = Object.keys(conteo);
  const valores = Object.values(conteo);
  const total = valores.reduce((a, b) => a + b, 0);

  const data = {
    labels: etiquetas.map((e, i) => `${e} - ${valores[i]}`),
    datasets: [{
      data: valores,
      backgroundColor: etiquetas.map((e, i) => colores ? (colores[e] || COLORES[i % COLORES.length]) : COLORES[i % COLORES.length]),
      borderColor: "#ffffff",
      borderWidth: 2,
    }],
  };

  const opciones = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right", labels: { boxWidth: 16, padding: 14, font: { weight: "bold", size: 13 }, color: "#1a1a2e" } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const valor = ctx.parsed || 0;
            const pct = total ? ((valor / total) * 100).toFixed(1) : "0.0";
            return ` ${valor} (${pct}%)`;
          },
        },
      },
      datalabels: {
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
        formatter: (valor) => (total ? `${((valor / total) * 100).toFixed(1)}%` : "0.0%"),
        color: "#1a1a2e",
        backgroundColor: "rgba(255,255,255,0.85)",
        borderRadius: 4,
        padding: { top: 3, bottom: 3, left: 6, right: 6 },
        font: { weight: "bold", size: 12 },
      },
    },
  };

  return <div style={s.graficaWrap}><Pie data={data} options={opciones} /></div>;
};

const GraficaBarras = ({ conteo }) => {
  const etiquetas = Object.keys(conteo);
  const valores = Object.values(conteo);

  const data = {
    labels: etiquetas,
    datasets: [{ label: "Tickets", data: valores, backgroundColor: "rgb(52, 152, 219)", borderRadius: 4 }],
  };

  const opciones = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, datalabels: { anchor: "end", align: "top", color: "#1a1a2e", font: { weight: "bold" } } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  return <div style={s.graficaWrapBarras}><Bar data={data} options={opciones} /></div>;
};

const EstadisticaTickets = memo(() => {
  const [tickets, setTickets] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const data = await obtenerTickets();
      const lista = Array.isArray(data) ? data : Object.values(data || {});
      setTickets(lista);
    } catch (error) {
      console.error("Error al cargar estadística de tickets:", error);
      setTickets([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, ChartDataLabels);
    cargarDatos();
  }, [cargarDatos]);

  if (cargando) return <AnimacionCarga />;

  const porEstado = contarPor(tickets, "estado_del_ticket");
  const porCategoria = contarPor(tickets, "categoria");
  const porFuncionario = contarPor(tickets, "nombre_funcionario");
  const promedioDias = promedioDiasAtencion(tickets);

  return (
    <div style={s.page}>
      <BarraSuperior />

      <div style={s.header}>
        <div>
          <h2 style={s.headerTitle}>Estadística de Tickets</h2>
          <p style={s.headerSub}>Total: {tickets.length} tickets{promedioDias !== null ? ` · Tiempo promedio de atención: ${promedioDias} días` : ""}</p>
        </div>
        <button onClick={cargarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
      </div>

      <div style={s.contenedor}>
        {tickets.length === 0 ? (
          <div style={s.vacio}>Aún no hay tickets registrados.</div>
        ) : (
          <>
            <div style={s.seccion}>
              <h3 style={s.seccionTitulo}>1. Tickets por Estado</h3>
              <GraficaTorta conteo={porEstado} colores={COLOR_ESTADO} />
            </div>
            <div style={s.seccion}>
              <h3 style={s.seccionTitulo}>2. Tickets por Categoría</h3>
              <GraficaTorta conteo={porCategoria} />
            </div>
            <div style={s.seccion}>
              <h3 style={s.seccionTitulo}>3. Tickets por Funcionario</h3>
              <GraficaBarras conteo={porFuncionario} />
            </div>
          </>
        )}
      </div>

      <BotonSOS />
    </div>
  );
});

const s = {
  page: { minHeight: "100vh", background: "#f8f9fb", fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif", paddingBottom: 100 },
  header: { maxWidth: 1100, margin: "0 auto", padding: "28px 24px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  headerTitle: { margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1a1a2e" },
  headerSub: { margin: 0, fontSize: 13, color: "#6b7280" },
  contenedor: { maxWidth: 900, margin: "0 auto", padding: 24 },
  seccion: { marginBottom: 40 },
  seccionTitulo: { fontSize: 18, fontWeight: 700, color: "#000000", textAlign: "center", margin: "0 0 16px" },
  graficaWrap: { height: 320, width: "100%" },
  graficaWrapBarras: { height: 320, width: "100%" },
  vacio: { textAlign: "center", padding: 60, color: "#9ca3af", fontSize: 15, maxWidth: 420, margin: "0 auto" },
};

export default EstadisticaTickets;
